/**
 * Product Catalog API Service
 */
import { apiClient } from './base';

// TypeScript Interfaces

export interface ProductListItem {
  product_id: string;
  company_id: string;
  code: string;
  description: string;
  strength_grade?: number;
  supplier_plant_id?: string;
  supplier_plant_name?: string;
  rev?: string;
  maturity_method?: string;
  /** The mix design's maturity group and the datum (Nurse-Saul) or Q (Arrhenius) it holds. */
  maturity_method_id?: string | null;
  maturity_group_id?: string | null;
  maturity_group_description?: string | null;
  maturity_datum_temp?: number | null;
  maturity_q?: number | null;
  is_active?: boolean;
  date_create?: string | null;
  create_by?: string | null;
}

export interface Product {
  product_id: string;
  company_id: string;
  code: string;
  description: string;
  supplier_plant_id?: string;
  strength_grade?: number;
  rev?: string;
  mass_concrete_default_id?: string;
  maturity_method_id?: string;
  maturity_method?: string;
  /**
   * Values held by the mix design's Nurse-Saul / Arrhenius group. The backend
   * copies these onto a pour when it is created, so they are what the pour's
   * maturity will be calculated with.
   */
  maturity_group_id?: string | null;
  maturity_group_description?: string | null;
  maturity_datum_temp?: number | null;
  maturity_activation_energy?: number | null;
  maturity_specified_temp?: number | null;
  maturity_q?: number | null;
  evaporation_default_id?: string;
  target_group_id?: string;
  supplier_plant_name?: string;
  is_active?: boolean;
  date_create?: string | null;
  create_by?: string | null;
  date_last_modified?: string | null;
  modified_by?: string | null;
}

export interface ProductCreate {
  code: string;
  description: string;
  company_id: string;
  supplier_plant_id?: string;
  strength_grade?: number;
  rev?: string;
  mass_concrete_default_id?: string;
  maturity_method_id?: string;
  maturity_method?: string;
  evaporation_default_id?: string;
  target_group_id?: string;
}

export interface ProductUpdate {
  code?: string;
  description?: string;
  supplier_plant_id?: string;
  strength_grade?: number;
  rev?: string;
  mass_concrete_default_id?: string;
  maturity_method_id?: string;
  evaporation_default_id?: string;
  target_group_id?: string;
  is_active?: boolean;
}

export interface ProductSearchParams {
  company_id?: string;
  code?: string;
  description?: string;
  supplier_plant_id?: string;
  is_active?: boolean;
  page?: number;
  page_size?: number;
}

export interface ProductPlantItem {
  supplier_plant_id: string;
  name: string;
  ready_mix_supplier?: string | null;
  date_linked?: string | null;
}

export interface TempProfileSourceItem {
  id: string;
  pour_id: string;
  pour_name?: string | null;
  pour_code?: string | null;
  location_id: string;
  location_description?: string | null;
}

export interface TempProfileResponse {
  identification?: string | null;
  sources: TempProfileSourceItem[];
  /** Nobody chose pours: these are the mix's most recent pours. */
  automatic?: boolean;
}

export interface TempProfileUpdate {
  identification?: string | null;
  sources: Array<{ pour_id: string; location_id: string }>;
}

export interface ProductListResponse {
  items: ProductListItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface SupplierPlant {
  supplier_plant_id: string;
  company_id: string;
  name: string;
  ready_mix_supplier?: string | null;
  address?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  is_active: boolean;
  date_create?: string | null;
  create_by?: string | null;
  date_last_modified?: string | null;
  modified_by?: string | null;
}

export interface SupplierPlantCreate {
  name: string;
  company_id: string;
  ready_mix_supplier?: string;
  address?: string;
  latitude?: string;
  longitude?: string;
}

export interface SupplierPlantUpdate {
  name?: string;
  ready_mix_supplier?: string | null;
  address?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  is_active?: boolean;
}

export interface SupplierPlantProductItem {
  product_id: string;
  code?: string | null;
  description?: string | null;
  strength_grade?: number | null;
  rev?: string | null;
  date_linked?: string | null;
}

export interface SupplierPlantDetail extends SupplierPlant {
  products: SupplierPlantProductItem[];
}

export interface TargetGroup {
  target_group_id: string;
  product_id: string;
  target_group_name?: string;
  create_date?: string;
  modify_date?: string;
}

export interface TargetGroupCreate {
  target_group_name: string;
}

export interface DefaultTarget {
  target_id: string;
  company_id: string;
  product_id?: string;
  target_group_id?: string;
  purpose: string;
  target?: number;
  calibration_id?: string;
  strength?: number;
  safety_margin?: number;
  base_strength?: number;
}

export interface TargetCreate {
  purpose: string;
  target?: number;
  calibration_id?: string;
  strength?: number;
  safety_margin?: number;
  base_strength?: number;
}

export interface TargetUpdate {
  purpose?: string;
  target?: number;
  calibration_id?: string;
  strength?: number;
  safety_margin?: number;
  base_strength?: number;
}

export interface CalibrationListItem {
  id: string;
  code: string;
  name?: string;
  company_id?: string;
  batched_time: string;
  sampled_time: string;
  create_date: string;
  calibration_type?: string;
}

export interface ProductCalibrationLink {
  id: string;
  product_id: string;
  calibration_id: string;
  active_from?: string;
  active_to?: string;
  is_active?: boolean;
  status?: string;
  calibration_code?: string;
  calibration_name?: string;
  activation_energy?: number;
  /** Calibration version number; null/undefined means v1. */
  version_no?: number | null;
  summary?: CalibrationLinkSummary | null;
}

/** Light per-row summary of a linked calibration (backend calibration_checks.summary). */
export interface CalibrationLinkSummary {
  calibration_type: 's' | 'm';
  sample_count: number;
  ages_tested: number;
  ages_recommended: number;
  maturity_min: number | null;
  maturity_max: number | null;
  curve_type: 'log' | 'linear';
  r2: number | null;
  basis_text: string | null;
  unit: string | null;
  /** Tested range of the curve (same shape as the pour payloads'). */
  calibrated_range?: import('./pours').CalibratedRange | null;
}

export interface CalibrationDetail {
  id: string;
  code: string;
  name?: string;
  regression_a: number | null;
  regression_b: number | null;
  curve_type?: 'log' | 'linear';
  linear_a?: number | null;
  linear_b?: number | null;
  r2_log?: number | null;
  r2_linear?: number | null;
}

export interface MassConcreteDefault {
  id: string;
  company_id: string;
  default_id: string;
  description: string;
  rev_no: string;
  date_create: string;
  create_by?: string;
  is_active?: boolean;
}

export interface MassConcreteDefaultLimit {
  id: string;
  mass_concrete_defaults_id: string;
  criteria: string;
  purpose: string;
  limits_type: string;
  limits_temp: number;
  background_color?: string;
  text_color?: string;
}

export interface MassConcreteDefaultLimitInput {
  /** Present for existing rows on update (drives diff/upsert); omitted for new rows. */
  id?: string;
  criteria: string;
  purpose: string;
  limits_type: string;
  limits_temp: number;
  background_color?: string;
  text_color?: string;
}

export interface MassConcreteDefaultDetail {
  id: string;
  company_id: string;
  default_id: string;
  description: string;
  date_create: string;
  create_by: string;
  date_last_modified: string;
  modified_by: string;
  rev_no: string;
  is_active?: boolean;
  limits: MassConcreteDefaultLimit[];
}

export interface MassConcreteDefaultCreate {
  company_id: string;
  description: string;
  /** Optional Group Code; auto-generated server-side when blank. */
  default_id?: string;
  is_active?: boolean;
  limits?: MassConcreteDefaultLimitInput[];
}

export interface MassConcreteDefaultUpdate {
  description?: string;
  /** Group Code (editable); revision number is auto-managed server-side. */
  default_id?: string;
  is_active?: boolean;
  limits?: MassConcreteDefaultLimitInput[];
}

export interface EvaporationDefault {
  id: string;
  company_id: string;
  default_id: string;
  description: string;
  rev_no: string;
  date_create: string;
  create_by?: string;
}

export interface EvaporationDefaultLimit {
  id: string;
  evaporation_defaults_id: string;
  purpose: string;
  limits_type: string;
  limits_rate: number;
  background_color?: string;
  text_color?: string;
}

export interface EvaporationDefaultLimitInput {
  purpose: string;
  limits_type: string;
  limits_rate: number;
  background_color?: string;
  text_color?: string;
}

export interface EvaporationDefaultDetail {
  id: string;
  company_id: string;
  default_id: string;
  description: string;
  date_create: string;
  create_by: string;
  date_last_modified: string;
  modified_by: string;
  rev_no: string;
  limits: EvaporationDefaultLimit[];
}

export interface EvaporationDefaultCreate {
  company_id: string;
  default_id?: string;
  description: string;
  rev_no?: string;
}

export interface EvaporationDefaultUpdate {
  default_id?: string;
  description?: string;
  rev_no?: string;
}

// API Service
const productsService = {
  // Product CRUD
  listProducts: async (companyId?: string, active?: boolean): Promise<ProductListItem[]> => {
    const params: Record<string, string | boolean> = {};
    if (companyId) params.company_id = companyId;
    if (active !== undefined) params.active = active;
    const response = await apiClient.get('/products/', { params });
    return response.data;
  },

  getProductPlants: async (productId: string): Promise<ProductPlantItem[]> => {
    const response = await apiClient.get(`/products/${productId}/supplier-plants`);
    return response.data;
  },

  getProductTempProfile: async (productId: string): Promise<TempProfileResponse> => {
    const response = await apiClient.get(`/products/${productId}/temp-profile`);
    return response.data;
  },

  updateProductTempProfile: async (productId: string, data: TempProfileUpdate): Promise<TempProfileResponse> => {
    const response = await apiClient.put(`/products/${productId}/temp-profile`, data);
    return response.data;
  },

  searchProducts: async (params: ProductSearchParams): Promise<ProductListResponse> => {
    const response = await apiClient.get('/products/search', { params });
    return response.data;
  },

  getProduct: async (productId: string): Promise<Product> => {
    const response = await apiClient.get(`/products/${productId}`);
    return response.data;
  },

  createProduct: async (data: ProductCreate): Promise<Product> => {
    const response = await apiClient.post('/products/', data);
    return response.data;
  },

  updateProduct: async (productId: string, data: ProductUpdate): Promise<Product> => {
    const response = await apiClient.put(`/products/${productId}`, data);
    return response.data;
  },

  deleteProduct: async (productId: string): Promise<void> => {
    await apiClient.delete(`/products/${productId}`);
  },

  // Supplier Plants (Batching Plants)
  listSupplierPlants: async (companyId?: string, active?: boolean): Promise<SupplierPlant[]> => {
    const params: Record<string, string | boolean> = {};
    if (companyId) params.company_id = companyId;
    if (active !== undefined) params.active = active;
    const response = await apiClient.get('/products/supplier-plants', { params });
    return response.data;
  },

  getSupplierPlant: async (plantId: string): Promise<SupplierPlantDetail> => {
    const response = await apiClient.get(`/products/supplier-plants/${plantId}`);
    return response.data;
  },

  createSupplierPlant: async (data: SupplierPlantCreate): Promise<SupplierPlant> => {
    const response = await apiClient.post('/products/supplier-plants', data);
    return response.data;
  },

  updateSupplierPlant: async (plantId: string, data: SupplierPlantUpdate): Promise<SupplierPlant> => {
    const response = await apiClient.put(`/products/supplier-plants/${plantId}`, data);
    return response.data;
  },

  deleteSupplierPlant: async (plantId: string): Promise<void> => {
    await apiClient.delete(`/products/supplier-plants/${plantId}`);
  },

  linkSupplierPlantProduct: async (plantId: string, productId: string): Promise<SupplierPlantProductItem> => {
    const response = await apiClient.post(`/products/supplier-plants/${plantId}/products/${productId}`);
    return response.data;
  },

  unlinkSupplierPlantProduct: async (plantId: string, productId: string): Promise<void> => {
    await apiClient.delete(`/products/supplier-plants/${plantId}/products/${productId}`);
  },

  // Target Groups
  listTargetGroups: async (productId: string): Promise<TargetGroup[]> => {
    const response = await apiClient.get(`/products/${productId}/target-groups`);
    return response.data;
  },

  createTargetGroup: async (productId: string, data: TargetGroupCreate): Promise<TargetGroup> => {
    const response = await apiClient.post(`/products/${productId}/target-groups`, data);
    return response.data;
  },

  updateTargetGroup: async (productId: string, groupId: string, data: TargetGroupCreate): Promise<TargetGroup> => {
    const response = await apiClient.put(`/products/${productId}/target-groups/${groupId}`, data);
    return response.data;
  },

  deleteTargetGroup: async (productId: string, groupId: string): Promise<void> => {
    await apiClient.delete(`/products/${productId}/target-groups/${groupId}`);
  },

  // Targets
  listTargets: async (productId: string, groupId: string): Promise<DefaultTarget[]> => {
    const response = await apiClient.get(`/products/${productId}/target-groups/${groupId}/targets`);
    return response.data;
  },

  createTarget: async (productId: string, groupId: string, data: TargetCreate): Promise<DefaultTarget> => {
    const response = await apiClient.post(`/products/${productId}/target-groups/${groupId}/targets`, data);
    return response.data;
  },

  updateTarget: async (productId: string, groupId: string, targetId: string, data: TargetUpdate): Promise<DefaultTarget> => {
    const response = await apiClient.put(`/products/${productId}/target-groups/${groupId}/targets/${targetId}`, data);
    return response.data;
  },

  deleteTarget: async (productId: string, groupId: string, targetId: string): Promise<void> => {
    await apiClient.delete(`/products/${productId}/target-groups/${groupId}/targets/${targetId}`);
  },

  // Calibrations (company-level)
  listCalibrations: async (companyId?: string): Promise<CalibrationListItem[]> => {
    const params = companyId ? { company_id: companyId } : {};
    const response = await apiClient.get('/products/calibrations', { params });
    return response.data;
  },

  // Fetch a single calibration (includes regression A/B for local conversions)
  getCalibration: async (calibrationId: string): Promise<CalibrationDetail> => {
    const response = await apiClient.get(`/products/calibrations/${calibrationId}`);
    return response.data;
  },

  // Product-Calibration Links
  listProductCalibrations: async (productId: string): Promise<ProductCalibrationLink[]> => {
    const response = await apiClient.get(`/products/${productId}/calibrations`);
    return response.data;
  },

  linkCalibration: async (productId: string, data: { calibration_id: string; is_active?: boolean }): Promise<ProductCalibrationLink> => {
    const response = await apiClient.post(`/products/${productId}/calibrations`, data);
    return response.data;
  },

  updateCalibrationLink: async (productId: string, linkId: string, data: { is_active?: boolean }): Promise<ProductCalibrationLink> => {
    const response = await apiClient.put(`/products/${productId}/calibrations/${linkId}`, data);
    return response.data;
  },

  deleteCalibrationLink: async (productId: string, linkId: string): Promise<void> => {
    await apiClient.delete(`/products/${productId}/calibrations/${linkId}`);
  },

  // Mass Concrete Defaults
  listMassConcreteDefaults: async (companyId?: string): Promise<MassConcreteDefault[]> => {
    const params = companyId ? { company_id: companyId } : {};
    const response = await apiClient.get('/products/mass-concrete-defaults', { params });
    return response.data;
  },

  getMassConcreteDefault: async (id: string): Promise<MassConcreteDefaultDetail> => {
    const response = await apiClient.get(`/products/mass-concrete-defaults/${id}`);
    return response.data;
  },

  createMassConcreteDefault: async (data: MassConcreteDefaultCreate): Promise<MassConcreteDefaultDetail> => {
    const response = await apiClient.post('/products/mass-concrete-defaults', data);
    return response.data;
  },

  updateMassConcreteDefault: async (id: string, data: MassConcreteDefaultUpdate): Promise<MassConcreteDefaultDetail> => {
    const response = await apiClient.put(`/products/mass-concrete-defaults/${id}`, data);
    return response.data;
  },

  deleteMassConcreteDefault: async (id: string): Promise<void> => {
    await apiClient.delete(`/products/mass-concrete-defaults/${id}`);
  },

  // Mass Concrete Defaults — child Limits
  listMassConcreteLimits: async (defaultId: string): Promise<MassConcreteDefaultLimit[]> => {
    const response = await apiClient.get(`/products/mass-concrete-defaults/${defaultId}/limits`);
    return response.data;
  },

  createMassConcreteLimit: async (
    defaultId: string,
    data: MassConcreteDefaultLimitInput,
  ): Promise<MassConcreteDefaultLimit> => {
    const response = await apiClient.post(`/products/mass-concrete-defaults/${defaultId}/limits`, data);
    return response.data;
  },

  updateMassConcreteLimit: async (
    defaultId: string,
    limitId: string,
    data: MassConcreteDefaultLimitInput,
  ): Promise<MassConcreteDefaultLimit> => {
    const response = await apiClient.put(
      `/products/mass-concrete-defaults/${defaultId}/limits/${limitId}`,
      data,
    );
    return response.data;
  },

  deleteMassConcreteLimit: async (defaultId: string, limitId: string): Promise<void> => {
    await apiClient.delete(`/products/mass-concrete-defaults/${defaultId}/limits/${limitId}`);
  },

  // Evaporation Defaults
  listEvaporationDefaults: async (companyId?: string): Promise<EvaporationDefault[]> => {
    const params = companyId ? { company_id: companyId } : {};
    const response = await apiClient.get('/products/evaporation-defaults', { params });
    return response.data;
  },

  getEvaporationDefault: async (id: string): Promise<EvaporationDefaultDetail> => {
    const response = await apiClient.get(`/products/evaporation-defaults/${id}`);
    return response.data;
  },

  createEvaporationDefault: async (data: EvaporationDefaultCreate): Promise<EvaporationDefaultDetail> => {
    const response = await apiClient.post('/products/evaporation-defaults', data);
    return response.data;
  },

  updateEvaporationDefault: async (id: string, data: EvaporationDefaultUpdate): Promise<EvaporationDefaultDetail> => {
    const response = await apiClient.put(`/products/evaporation-defaults/${id}`, data);
    return response.data;
  },

  deleteEvaporationDefault: async (id: string): Promise<void> => {
    await apiClient.delete(`/products/evaporation-defaults/${id}`);
  },

  // Evaporation Defaults — child Limits
  listEvaporationLimits: async (defaultId: string): Promise<EvaporationDefaultLimit[]> => {
    const response = await apiClient.get(`/products/evaporation-defaults/${defaultId}/limits`);
    return response.data;
  },

  createEvaporationLimit: async (
    defaultId: string,
    data: EvaporationDefaultLimitInput,
  ): Promise<EvaporationDefaultLimit> => {
    const response = await apiClient.post(`/products/evaporation-defaults/${defaultId}/limits`, data);
    return response.data;
  },

  updateEvaporationLimit: async (
    defaultId: string,
    limitId: string,
    data: EvaporationDefaultLimitInput,
  ): Promise<EvaporationDefaultLimit> => {
    const response = await apiClient.put(
      `/products/evaporation-defaults/${defaultId}/limits/${limitId}`,
      data,
    );
    return response.data;
  },

  deleteEvaporationLimit: async (defaultId: string, limitId: string): Promise<void> => {
    await apiClient.delete(`/products/evaporation-defaults/${defaultId}/limits/${limitId}`);
  },
};

export default productsService;
